import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrackRow } from "@/components/TrackRow";
import { usePlayer, type Track } from "@/stores/player";
import { Music2, Play } from "lucide-react";

export const Route = createFileRoute("/_authed/album/$id")({
  head: () => ({ meta: [{ title: "Album — Sura" }] }),
  component: AlbumPage,
});

function colorFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return [220, 250, 200, 180, 270, 230][h % 6];
}

function AlbumPage() {
  const { id } = Route.useParams();
  const [album, setAlbum] = useState<any>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const playTrack = usePlayer((s) => s.playTrack);

  useEffect(() => {
    api.get(`/api/albums/${id}`).then(({ data }) => {
      const a = data.album || data;
      setAlbum(a);
      setTracks(a.tracks || []);
    });
  }, [id]);

  if (!album) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  const hue = colorFor(album.id);

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start mb-8">
        <div
          className="w-44 h-44 md:w-56 md:h-56 rounded-2xl flex items-center justify-center text-primary-foreground shadow-[var(--shadow-soft)] shrink-0"
          style={{ background: `linear-gradient(135deg, oklch(0.65 0.16 ${hue}), oklch(0.5 0.18 ${hue + 30}))` }}
        >
          <Music2 className="h-16 w-16 opacity-80" />
        </div>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Album</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">{album.title}</h1>
          {album.description && <p className="mt-3 text-muted-foreground max-w-xl">{album.description}</p>}
          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={() => tracks[0] && playTrack(tracks[0], tracks)}
              disabled={!tracks.length}
              className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium text-sm flex items-center gap-2 hover:bg-primary/90 disabled:opacity-50"
            >
              <Play className="h-4 w-4 fill-current" /> Play
            </button>
            <span className="text-sm text-muted-foreground">{tracks.length} tracks</span>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-2">
        {tracks.map((t, i) => <TrackRow key={t.id} track={{ ...t, album: { id: album.id, title: album.title } }} index={i} queue={tracks} />)}
      </div>
    </div>
  );
}
